import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert,
  Switch,
  Link
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useAuth } from '../../components/auth/AuthProvider';
import { useValueChainMetaInfo } from '../shared/hooks/useValueChainMetaInfo';
import { useDisabledValueChainNames } from '../shared/hooks/useDisabledValueChainNames';
import SankeyDiagram from '../shared/components/SankeyDiagram';
import { api } from '../../api/setup';
import apiClient from '../../api/client';

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

interface GraphNode {
  id?: string;
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  source_db?: string;
}

interface SankeyData {
  nodes: Array<{
    name: any;
    value?: number;
    itemStyle?: { color: string };
    depth?: number;
    label?: any;
    nodeData: {
      type: any;
      properties: any;
      elementId: any;
      n_iteration: any;
    };
  }>;
  links: any[];
}

const QUANTITY_INDICATOR_CODES = new Set([
  'PRODQNT',
  'PQNTBASE',
  'SCPRODQNT',
  'SCQNTBASE',
  'OWNPRODQNT',
  'OWNQNTBASE',
  'EXPQNT',
  'IMPQNT',
]);
const MONETARY_INDICATOR_CODES = new Set([
  'PRODVAL',
  'IMPVAL',
  'EXPVAL',
  'OWNPRODVAL',
  'PVALBASE',
  'SCPRODVAL',
  'SCVALBASE',
  'OWNVALBASE',
]);
const DECIMAL_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const EURO_CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const COMPACT_EURO_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const SELECT_MENU_PROPS = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: { xs: '56vh', md: 320 },
      width: { xs: 'min(92vw, 420px)', md: 'auto' }
    }
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' } as const,
  transformOrigin: { vertical: 'top', horizontal: 'left' } as const,
  MenuListProps: { dense: true }
};

interface ProdcomReporterOption {
  country_code: string;
  country_name: string;
}

interface ProdcomIndicatorOption {
  code: string;
  name_en: string;
}

interface ProdcomFactYearOption {
  fact_year: number;
  version_code: string;
}

interface ProdcomObservation {
  indicators?: Record<string, {
    value: number | string | null;
    qntunit?: string | null;
  }>;
}

const DEFAULT_REPORTER_CODE_PREFERENCES = [
  'EU27_2020',
  'EU27',
];

const parseProdcomMappings = (raw: any): Record<string, any[]> => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, any[]>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const getVersionProdcomEntry = (properties: Record<string, any>, version: string): { code?: string; description?: string } => {
  const mappings = parseProdcomMappings(properties?.prodcom_mappings);
  const entries = mappings?.[version];
  if (!Array.isArray(entries)) return {};
  const match = entries.find((e: any) => typeof e?.code === 'string' && e.code.trim().length > 0);
  if (!match) return {};
  return {
    code: typeof match.code === 'string' ? match.code.trim() : undefined,
    description: typeof match.description === 'string' ? match.description.trim() : undefined,
  };
};

/** Backend returns versioned flat props (prodcom_selected_code / prodcom_selected_description) for the requested version. */
const hasProdcomInGraph = (nodes: any[]): boolean => {
  return nodes.some((n: any) => {
    const members = Array.isArray(n?.properties?.members) ? n.properties.members : [];
    return members.some((m: any) => {
      const code = m?.properties?.prodcom_selected_code;
      return typeof code === 'string' && code.trim().length > 0;
    });
  });
};

const formatIndicatorDisplayValue = (
  indicatorCode: string,
  rawValue: number | string | null | undefined,
  qntunit?: string | null
): string => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return '—';

  const normalizedCode = String(indicatorCode || '').toUpperCase();
  const numericValue = typeof rawValue === 'number'
    ? rawValue
    : Number(String(rawValue).replace(/,/g, '').trim());
  const isNumeric = Number.isFinite(numericValue);

  if (MONETARY_INDICATOR_CODES.has(normalizedCode) && isNumeric) {
    return EURO_CURRENCY_FORMATTER.format(numericValue);
  }

  if (QUANTITY_INDICATOR_CODES.has(normalizedCode) && isNumeric) {
    const valueText = DECIMAL_NUMBER_FORMATTER.format(numericValue);
    const unitText = qntunit ? ` ${String(qntunit).trim()}` : '';
    return `${valueText}${unitText}`;
  }

  if (isNumeric) {
    return DECIMAL_NUMBER_FORMATTER.format(numericValue);
  }

  return String(rawValue);
};

const formatLegendValue = (indicatorCode: string, rawValue: number): string => {
  const normalizedCode = String(indicatorCode || '').toUpperCase();
  if (!Number.isFinite(rawValue)) return '—';
  if (MONETARY_INDICATOR_CODES.has(normalizedCode)) {
    return EURO_CURRENCY_FORMATTER.format(rawValue);
  }
  return DECIMAL_NUMBER_FORMATTER.format(rawValue);
};

const formatLegendCompactValue = (indicatorCode: string, rawValue: number): string => {
  const normalizedCode = String(indicatorCode || '').toUpperCase();
  if (!Number.isFinite(rawValue)) return '—';
  if (MONETARY_INDICATOR_CODES.has(normalizedCode)) {
    return COMPACT_EURO_FORMATTER.format(rawValue);
  }
  return COMPACT_NUMBER_FORMATTER.format(rawValue);
};

const pickDefaultReporterCode = (reporters: ProdcomReporterOption[]): string => {
  if (!Array.isArray(reporters) || reporters.length === 0) return '';

  const byPreferredCode = reporters.find((r) =>
    DEFAULT_REPORTER_CODE_PREFERENCES.includes(String(r.country_code || '').toUpperCase())
  );
  if (byPreferredCode) return byPreferredCode.country_code;

  const byNameMatch = reporters.find((r) => {
    const normalized = String(r.country_name || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return normalized.includes('eu 27 countries') && normalized.includes('2020');
  });
  if (byNameMatch) return byNameMatch.country_code;

  const byEu27Name = reporters.find((r) => {
    const normalized = String(r.country_name || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return normalized.includes('european union') && normalized.includes('27') && normalized.includes('2020');
  });
  if (byEu27Name) return byEu27Name.country_code;

  return reporters[0]?.country_code || '';
};

const isProdcomColorIndicator = (code: string): boolean => {
  const normalized = String(code || '').toUpperCase();
  if (!normalized) return false;
  if (normalized.endsWith('FLAG')) return false;
  if (normalized.endsWith('UNIT')) return false;
  return true;
};

const ProdcomGroupedProdcomCodeAnalysis: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedElementId, setSelectedElementId] = useState<string>('');
  const [availableRoots, setAvailableRoots] = useState<GraphNode[]>([]);
  const [graphData, setGraphData] = useState<SankeyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [maxIterations, setMaxIterations] = useState<number>(isMobile ? 2 : 5);
  const [minFlowThreshold, setMinFlowThreshold] = useState<number>(0.001);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [selectedMembers, setSelectedMembers] = useState<GraphNode[]>([]);
  const [clearFocusTick, setClearFocusTick] = useState<number>(0);
  const [hasProdcomCodes, setHasProdcomCodes] = useState<boolean>(true);
  const [availableReporters, setAvailableReporters] = useState<ProdcomReporterOption[]>([]);
  const [availableFactYears, setAvailableFactYears] = useState<ProdcomFactYearOption[]>([]);
  const [availableIndicators, setAvailableIndicators] = useState<ProdcomIndicatorOption[]>([]);
  const [selectedReporterCode, setSelectedReporterCode] = useState<string>('');
  const [selectedFactYear, setSelectedFactYear] = useState<string>('');
  const [selectedIndicatorCode, setSelectedIndicatorCode] = useState<string>('');
  const [useLogColorScale, setUseLogColorScale] = useState<boolean>(false);
  const [selectorOptionsLoading, setSelectorOptionsLoading] = useState<boolean>(false);
  const [prodcomObservationsByCode, setProdcomObservationsByCode] = useState<Record<string, ProdcomObservation>>({});
  const [prodcomFactsLoading, setProdcomFactsLoading] = useState<boolean>(false);
  const [prodcomFactsError, setProdcomFactsError] = useState<string | null>(null);
  const [prodcomExportLoading, setProdcomExportLoading] = useState<boolean>(false);
  const [prodcomExportError, setProdcomExportError] = useState<string | null>(null);
  const [prodcomFactsMatchedCount, setProdcomFactsMatchedCount] = useState<number>(0);
  const [prodcomFactsVersionCode, setProdcomFactsVersionCode] = useState<string>('');
  const [prodcomFactsUnmatchedCodesSample, setProdcomFactsUnmatchedCodesSample] = useState<string[]>([]);

  const [selectedRootElementId, setSelectedRootElementId] = useState<string>('');
  const [selectedRootName, setSelectedRootName] = useState<string>('');
  const [selectedRootSourceDb, setSelectedRootSourceDb] = useState<string>('');
  const STORAGE_ID_KEY = 'vc.selectedRootId';
  const STORAGE_NAME_KEY = 'vc.selectedRootName';
  const STORAGE_SOURCE_DB_KEY = 'vc.selectedRootSourceDb';

  const { info: valueChainInfo, shortcomings: valueChainShortcomings } = useValueChainMetaInfo(selectedRootSourceDb);
  const disabledValueChainNames = useDisabledValueChainNames();

  const normalizePayloadArray = (resp: any): any[] => {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    if (Array.isArray(resp?.data?.items)) return resp.data.items;
    if (Array.isArray(resp?.items)) return resp.items;
    return [];
  };

  useEffect(() => {
    let cancelled = false;

    const loadSelectorOptions = async () => {
      setSelectorOptionsLoading(true);
      try {
        const [reportersResp, yearsResp, indicatorsResp] = await Promise.all([
          api.request.request<any>({ method: 'GET', url: '/api/v1/prodcom/countries' }),
          api.request.request<any>({ method: 'GET', url: '/api/v1/prodcom/fact-years' }),
          api.request.request<any>({ method: 'GET', url: '/api/v1/prodcom/indicators' }),
        ]);

        const reporterRows = normalizePayloadArray(reportersResp);
        const yearRows = normalizePayloadArray(yearsResp);
        const indicatorRows = normalizePayloadArray(indicatorsResp);

        const reporters = reporterRows
          .map((row: any) => ({
            country_code: String(row?.country_code ?? '').trim(),
            country_name: String(row?.country_name ?? row?.country_code ?? '').trim(),
          }))
          .filter((row: ProdcomReporterOption) => row.country_code.length > 0)
          .sort((a: ProdcomReporterOption, b: ProdcomReporterOption) => a.country_name.localeCompare(b.country_name));

        const years = Array.from(new Set(
          yearRows
            .map((row: any) => ({
              fact_year: Number(row?.fact_year ?? row?.year ?? row),
              version_code: String(row?.version_code ?? '').trim(),
            }))
            .filter((row: ProdcomFactYearOption) => Number.isFinite(row.fact_year) && row.version_code.length > 0)
            .map((row: ProdcomFactYearOption) => `${row.fact_year}||${row.version_code}`)
        ))
          .map((key) => {
            const [yearPart, versionPart] = String(key).split('||');
            return { fact_year: Number(yearPart), version_code: versionPart };
          })
          .sort((a: ProdcomFactYearOption, b: ProdcomFactYearOption) => b.fact_year - a.fact_year);

        const indicators = indicatorRows
          .map((row: any) => ({
            code: String(row?.code ?? '').trim(),
            name_en: String(row?.name_en ?? row?.code ?? '').trim(),
          }))
          .filter((row: ProdcomIndicatorOption) => row.code.length > 0);

        if (!cancelled) {
          setAvailableReporters(reporters);
          setAvailableFactYears(years);
          setAvailableIndicators(indicators);
          setSelectedReporterCode((prev) => (
            prev && reporters.some((r) => r.country_code === prev)
              ? prev
              : pickDefaultReporterCode(reporters)
          ));
          setSelectedFactYear((prev) => (
            prev && years.some((y) => String(y.fact_year) === String(prev))
              ? prev
              : (years[0] != null ? String(years[0].fact_year) : '')
          ));
          setSelectedIndicatorCode((prev) => (
            prev && indicators.some((i) => i.code === prev && isProdcomColorIndicator(i.code))
              ? prev
              : ((indicators.find((i) => isProdcomColorIndicator(i.code))?.code) ?? '')
          ));
        }
      } catch {
        if (!cancelled) {
          setAvailableReporters([]);
          setAvailableFactYears([]);
          setAvailableIndicators([]);
          setSelectedIndicatorCode('');
        }
      } finally {
        if (!cancelled) setSelectorOptionsLoading(false);
      }
    };

    loadSelectorOptions();
    return () => { cancelled = true; };
  }, []);

  const handleRootChange = (eventOrValue) => {
    const newId = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue?.target?.value;
    if (newId == null) return;
    setSelectedElementId(String(newId));
    const n = (availableRoots || []).find((x) => String(x.element_id) === String(newId));
    const name = n ? n.name : `Element ${newId}`;
    const sourceDb = n?.source_db || '';
    setSelectedRootElementId(String(newId));
    setSelectedRootName(String(name));
    setSelectedRootSourceDb(String(sourceDb));
    try {
      localStorage.setItem(STORAGE_ID_KEY, String(newId));
      localStorage.setItem(STORAGE_NAME_KEY, String(name));
      localStorage.setItem(STORAGE_SOURCE_DB_KEY, String(sourceDb));
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (SHOULD_DEBUG) console.log('Fetching roots, isAuthenticated:', isAuthenticated);
        const response = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
        const roots = Array.isArray(response?.data) ? response.data :
                     Array.isArray(response) ? response : [];
        setAvailableRoots(roots);

        if (selectedElementId) {
          const isStillAvailable = roots.some((r: any) => String(r.element_id) === String(selectedElementId));
          if (!isStillAvailable) {
            setSelectedElementId('');
            setSelectedRootElementId('');
            setSelectedRootName('');
            setSelectedRootSourceDb('');
            setGraphData(null);
            if (roots.length > 0) {
              const firstPublic = roots[0];
              setSelectedElementId(String(firstPublic.element_id));
              setSelectedRootElementId(String(firstPublic.element_id));
              setSelectedRootName(String(firstPublic.name));
              setSelectedRootSourceDb(String(firstPublic.source_db || ''));
            }
          }
        }

        const savedId = String(localStorage.getItem(STORAGE_ID_KEY) || '');
        const savedName = String(localStorage.getItem(STORAGE_NAME_KEY) || '');
        const savedSourceDb = String(localStorage.getItem(STORAGE_SOURCE_DB_KEY) || '');
        if (!selectedElementId) {
          if (savedId) {
            const matchedRoot = roots.find((r: any) => String(r.element_id) === savedId);
            if (matchedRoot) {
              setSelectedElementId(String(savedId));
              setSelectedRootElementId(String(savedId));
              setSelectedRootName(savedName || (matchedRoot?.name || ''));
              setSelectedRootSourceDb(savedSourceDb || (matchedRoot?.source_db || ''));
            } else if (roots.length > 0) {
              const first = roots[0];
              setSelectedElementId(String(first.element_id));
              setSelectedRootElementId(String(first.element_id));
              setSelectedRootName(String(first.name));
              setSelectedRootSourceDb(String(first.source_db || ''));
            }
          } else if (roots.length > 0) {
            const first = roots[0];
            setSelectedElementId(String(first.element_id));
            setSelectedRootElementId(String(first.element_id));
            setSelectedRootName(String(first.name));
            setSelectedRootSourceDb(String(first.source_db || ''));
          }
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('Error loading roots:', error);
        setAvailableRoots([]);
      }
    })();
  }, [isAuthenticated]);

  const resolvedGraphProdcomVersion = useMemo(() => {
    const option = availableFactYears.find((row) => String(row.fact_year) === String(selectedFactYear));
    return option?.version_code || '2024';
  }, [availableFactYears, selectedFactYear]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!selectedElementId) { if (!cancelled) setGraphData(null); return; }

      setLoading(true); setError(null);
      try {
        const resp = await api.graphData.getIncomingGroupedByProdcomApiV1GraphIncomingGroupedProdcomElementIdGet(
          String(selectedElementId),
          maxIterations,
          minFlowThreshold,
          resolvedGraphProdcomVersion
        );
        if (!resp || !resp.success || !resp.data) {
          if (!cancelled) { setGraphData({ nodes: [], links: [] }); setHasProdcomCodes(false); }
          return;
        }
        const data = resp.data;
        const anyProdcom = hasProdcomInGraph(Array.isArray(data.nodes) ? data.nodes : []);
        const idToName = new Map();
        const nodes = (data.nodes || []).map(n => {
          const label = n.properties?.name || n.id;
          idToName.set(n.id, label);
          const match = typeof label === 'string' ? label.match(/^\s*(\d+)\s*:/) : null;
          const nIteration = match ? parseInt(match[1], 10) : undefined;
          return { name: label, nodeData: { type: 'Grouped', elementId: n.id, n_iteration: nIteration, properties: n.properties || {} } };
        });
        const links = (data.relationships || []).map(r => ({
          source: idToName.get(r.start_node_id) || r.start_node_id,
          target: idToName.get(r.end_node_id) || r.end_node_id,
          value: r.properties?.path_weight ?? 0
        }));
        if (!cancelled) {
          setGraphData({ nodes, links });
          setHasProdcomCodes(anyProdcom);
        }
      } catch (e) {
        if (SHOULD_DEBUG) console.error(e);
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to fetch grouped PRODCOM graph');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [selectedElementId, maxIterations, minFlowThreshold, resolvedGraphProdcomVersion, availableRoots, isAuthenticated]);

  const prevThresholdRef = useRef(minFlowThreshold);
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      let cancelled = false;
      const fetchData = async () => {
        setLoading(true); setError(null);
        try {
          const resp = await api.graphData.getIncomingGroupedByProdcomApiV1GraphIncomingGroupedProdcomElementIdGet(
            String(selectedElementId),
            maxIterations,
            minFlowThreshold,
            resolvedGraphProdcomVersion
          );
          if (!resp || !resp.success || !resp.data) {
            if (!cancelled) { setGraphData({ nodes: [], links: [] }); setHasProdcomCodes(false); }
            return;
          }
          const data = resp.data;
          const anyProdcom = hasProdcomInGraph(Array.isArray(data.nodes) ? data.nodes : []);
          const idToName = new Map();
          const nodes = (data.nodes || []).map(n => {
            const label = n.properties?.name || n.id;
            idToName.set(n.id, label);
            const match = typeof label === 'string' ? label.match(/^\s*(\d+)\s*:/) : null;
            const nIteration = match ? parseInt(match[1], 10) : undefined;
            return { name: label, nodeData: { type: 'Grouped', elementId: n.id, n_iteration: nIteration, properties: n.properties || {} } };
          });
          const links = (data.relationships || []).map(r => ({
            source: idToName.get(r.start_node_id) || r.start_node_id,
            target: idToName.get(r.end_node_id) || r.end_node_id,
            value: r.properties?.path_weight ?? 0
          }));
          if (!cancelled) {
            setGraphData({ nodes, links });
            setHasProdcomCodes(anyProdcom);
          }
        } catch (e) {
          if (SHOULD_DEBUG) console.error(e);
          if (!cancelled) setError(e.response?.data?.detail || 'Failed to fetch grouped PRODCOM graph');
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      fetchData();
      return () => { cancelled = true; };
    }
    prevThresholdRef.current = minFlowThreshold;
  }, [minFlowThreshold, selectedElementId, maxIterations, resolvedGraphProdcomVersion]);

  const displayedProdcomCodes = useMemo(() => {
    if (!graphData?.nodes?.length) return [] as string[];
    const seen = new Set<string>();
    graphData.nodes.forEach((node) => {
      const members = Array.isArray(node?.nodeData?.properties?.members) ? node.nodeData.properties.members : [];
      members.forEach((member: any) => {
        const code = String(member?.properties?.prodcom_selected_code ?? '').trim();
        if (code) seen.add(code);
      });
    });
    return Array.from(seen).sort();
  }, [graphData]);

  const colorIndicatorOptions = useMemo(
    () => availableIndicators.filter((i) => isProdcomColorIndicator(i.code)),
    [availableIndicators]
  );
  const selectedIndicator = useMemo(
    () => colorIndicatorOptions.find((i) => i.code === selectedIndicatorCode) || null,
    [colorIndicatorOptions, selectedIndicatorCode]
  );
  const tableIndicatorCodes = useMemo(
    () => availableIndicators
      .map((i) => i.code)
      .filter((code) => !String(code).toUpperCase().endsWith('FLAG')),
    [availableIndicators]
  );
  const prodcomPathWeightByCode = useMemo(() => {
    if (!graphData?.nodes?.length || !graphData?.links?.length) return {} as Record<string, number>;

    const nodeNameToCode = new Map<string, string>();
    graphData.nodes.forEach((node) => {
      const nodeName = String(node?.name ?? '').trim();
      const members = Array.isArray(node?.nodeData?.properties?.members) ? node.nodeData.properties.members : [];
      let code = String(members?.[0]?.properties?.prodcom_selected_code ?? '').trim();
      if (!code) {
        const match = nodeName.match(/:\s*(.+)\s*$/);
        code = String(match?.[1] ?? '').trim();
      }
      if (!code || code === '(no-prodcom)') return;
      nodeNameToCode.set(nodeName, code);
    });

    const out: Record<string, number> = {};
    graphData.links.forEach((link: any) => {
      const sourceName = String(link?.source ?? '').trim();
      const code = nodeNameToCode.get(sourceName);
      if (!code) return;
      const value = Number(link?.value ?? link?.properties?.path_weight ?? 0);
      if (!Number.isFinite(value) || value <= 0) return;
      out[code] = (out[code] || 0) + value;
    });

    return out;
  }, [graphData]);
  const prodcomPathWeightTotal = useMemo(
    () => Object.values(prodcomPathWeightByCode).reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0),
    [prodcomPathWeightByCode]
  );

  const displayedProdcomRows = useMemo(() => {
    const descriptionByCode = new Map<string, string>();
    graphData?.nodes?.forEach((node) => {
      const members = Array.isArray(node?.nodeData?.properties?.members) ? node.nodeData.properties.members : [];
      members.forEach((member: any) => {
        const code = String(member?.properties?.prodcom_selected_code ?? '').trim();
        if (!code || descriptionByCode.has(code)) return;
        const desc = String(
          member?.properties?.prodcom_selected_description ??
          member?.properties?.prodcom_description ??
          ''
        ).trim();
        if (desc) descriptionByCode.set(code, desc);
      });
    });

    return displayedProdcomCodes
      .map((code) => {
        const obs = prodcomObservationsByCode?.[code];
        const pathWeight = Number(prodcomPathWeightByCode?.[code] || 0);
        return {
          code,
          valueShare: prodcomPathWeightTotal > 0 && Number.isFinite(pathWeight) && pathWeight > 0
            ? (pathWeight / prodcomPathWeightTotal)
            : 0,
          description: descriptionByCode.get(code) || '',
          indicators: obs?.indicators || {},
        };
      })
      .sort((a, b) => {
        const delta = Number(b.valueShare || 0) - Number(a.valueShare || 0);
        if (delta !== 0) return delta;
        return String(a.code).localeCompare(String(b.code));
      });
  }, [displayedProdcomCodes, graphData, prodcomObservationsByCode, prodcomPathWeightByCode, prodcomPathWeightTotal]);

  const prodcomColorByCode = useMemo(() => {
    if (!selectedIndicatorCode) return {} as Record<string, number>;
    const out: Record<string, number> = {};
    displayedProdcomCodes.forEach((code) => {
      const raw = prodcomObservationsByCode?.[code]?.indicators?.[selectedIndicatorCode]?.value;
      const value = typeof raw === 'number' ? raw : Number(String(raw ?? '').replace(/,/g, '').trim());
      if (Number.isFinite(value)) out[code] = value;
    });
    return out;
  }, [displayedProdcomCodes, prodcomObservationsByCode, selectedIndicatorCode]);

  const prodcomTooltipByCode = useMemo(() => {
    const itemNamesByCode = new Map<string, Set<string>>();
    graphData?.nodes?.forEach((node) => {
      const members = Array.isArray(node?.nodeData?.properties?.members) ? node.nodeData.properties.members : [];
      members.forEach((member: any) => {
        const code = String(member?.properties?.prodcom_selected_code ?? '').trim();
        const labels = Array.isArray(member?.labels) ? member.labels.map((l: any) => String(l)) : [];
        const isProdcomCarrier = labels.includes('TechnologyType') || labels.includes('NoProductTechnologyType');
        const memberName = String(member?.properties?.name ?? '').trim();
        if (!isProdcomCarrier || !code || !memberName) return;
        if (!itemNamesByCode.has(code)) itemNamesByCode.set(code, new Set<string>());
        itemNamesByCode.get(code)?.add(memberName);
      });
    });

    const out: Record<string, { description?: string; indicatorLabel?: string; valueText?: string; aggregatedItemNames?: string[] }> = {};
    if (!selectedIndicator) return out;
    displayedProdcomRows.forEach((row) => {
      const indicatorObs = row.indicators?.[selectedIndicator.code];
      out[row.code] = {
        description: row.description || '',
        indicatorLabel: selectedIndicator.name_en,
        valueText: formatIndicatorDisplayValue(selectedIndicator.code, indicatorObs?.value, indicatorObs?.qntunit),
        aggregatedItemNames: Array.from(itemNamesByCode.get(row.code) || []).sort((a, b) => a.localeCompare(b)),
      };
    });
    return out;
  }, [displayedProdcomRows, selectedIndicator, graphData]);
  const prodcomTooltipByNodeKey = useMemo(() => {
    const out: Record<string, { description?: string; indicatorLabel?: string; valueText?: string; aggregatedItemNames?: string[] }> = {};
    if (!selectedIndicator) return out;

    graphData?.nodes?.forEach((node) => {
      const nodeKey = String(node?.name ?? node?.nodeData?.properties?.name ?? '').trim();
      if (!nodeKey) return;
      const members = Array.isArray(node?.nodeData?.properties?.members) ? node.nodeData.properties.members : [];
      const codeFromMembers = String(members?.[0]?.properties?.prodcom_selected_code ?? '').trim();
      const codeFromName = String(nodeKey.split(':').slice(1).join(':') || '').trim();
      const code = codeFromMembers || codeFromName;
      if (!code) return;

      const row = displayedProdcomRows.find((r) => r.code === code);
      const indicatorObs = row?.indicators?.[selectedIndicator.code];
      const contributingMembers = members.filter((m: any) => {
        const labels = Array.isArray(m?.labels) ? m.labels.map((l: any) => String(l)) : [];
        const isProdcomCarrier = labels.includes('TechnologyType') || labels.includes('NoProductTechnologyType');
        const memberCode = String(m?.properties?.prodcom_selected_code ?? '').trim();
        return isProdcomCarrier && memberCode === code;
      });
      const itemNames: string[] = Array.from(new Set(
        contributingMembers
          .map((m: any) => String(m?.properties?.name ?? '').trim())
          .filter((name: string) => Boolean(name))
      )) as string[];
      itemNames.sort((a: string, b: string) => a.localeCompare(b));

      out[nodeKey] = {
        description: row?.description || String(node?.nodeData?.properties?.prodcom_description || '').trim(),
        indicatorLabel: selectedIndicator.name_en,
        valueText: formatIndicatorDisplayValue(selectedIndicator.code, indicatorObs?.value, indicatorObs?.qntunit),
        aggregatedItemNames: itemNames,
      };
    });

    return out;
  }, [graphData, displayedProdcomRows, selectedIndicator]);

  const prodcomExportHeaders = useMemo(() => {
    const indicatorHeaders = tableIndicatorCodes.map((indicatorCode) => {
      const indicator = availableIndicators.find((i) => i.code === indicatorCode);
      return indicator?.name_en || indicatorCode;
    });
    return ['Code', 'Value share', 'Description', ...indicatorHeaders];
  }, [tableIndicatorCodes, availableIndicators]);

  const prodcomExportRows = useMemo(() => (
    displayedProdcomRows.map((row) => ([
      row.code,
      PERCENT_FORMATTER.format(Number(row.valueShare || 0)),
      row.description || '—',
      ...tableIndicatorCodes.map((indicatorCode) => {
        const indicatorObs = row.indicators?.[indicatorCode];
        return formatIndicatorDisplayValue(indicatorCode, indicatorObs?.value, indicatorObs?.qntunit);
      }),
    ]))
  ), [displayedProdcomRows, tableIndicatorCodes]);

  const handleExportProdcomObservations = async () => {
    if (prodcomExportRows.length === 0 || prodcomExportHeaders.length === 0) return;
    setProdcomExportLoading(true);
    setProdcomExportError(null);
    try {
      const safeRoot = String(selectedRootName || 'value_chain').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safeReporter = String(selectedReporterCode || 'reporter').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const safeYear = String(selectedFactYear || 'year').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const requestedFilename = `prodcom_observations_${safeRoot}_${safeReporter}_${safeYear}.xlsx`;
      const reporterName = availableReporters.find((r) => r.country_code === selectedReporterCode)?.country_name || selectedReporterCode || '—';
      const indicatorName = selectedIndicator?.name_en || selectedIndicatorCode || '—';
      const metaRows = [
        ['Value chain', selectedRootName || '—'],
        ['N Layers', String(maxIterations)],
        ['Min Value (%)', (minFlowThreshold * 100).toFixed(1)],
        ['Reporter', reporterName],
        ['Year', selectedFactYear || '—'],
        ['Indicator (color)', indicatorName],
        ['PRODCOM version', resolvedGraphProdcomVersion || '—'],
        ['Source', 'Productive Capabilities Data Lab, valuechains.ai'],
        ['Date of download', new Date().toISOString()],
      ];

      const response = await apiClient.authenticatedPost('/api/v1/prodcom/export-xlsx', {
          headers: prodcomExportHeaders,
          rows: prodcomExportRows,
          meta_rows: metaRows,
          filename: requestedFilename,
      });
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const contentType = String(response.headers.get('content-type') || '');
      if (contentType.includes('application/json')) {
        const errPayload: any = await response.json();
        if (errPayload?.success === false) {
          throw new Error(String(errPayload?.message || 'Failed to export XLSX'));
        }
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || requestedFilename;

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setProdcomExportError(error?.message || 'Failed to export XLSX');
    } finally {
      setProdcomExportLoading(false);
    }
  };

  const prodcomColorCap = useMemo(() => {
    let cap = 0;
    Object.values(prodcomColorByCode).forEach((v) => {
      if (Number.isFinite(v)) cap = Math.max(cap, Number(v));
    });
    return cap;
  }, [prodcomColorByCode]);
  const prodcomLegendMidValue = useMemo(() => {
    if (!Number.isFinite(prodcomColorCap) || prodcomColorCap <= 0) return 0;
    if (useLogColorScale) {
      // Midpoint in log1p space to match the color interpolation mode.
      return Math.expm1(Math.log1p(prodcomColorCap) / 2);
    }
    return prodcomColorCap / 2;
  }, [prodcomColorCap, useLogColorScale]);

  useEffect(() => {
    let cancelled = false;
    const fetchProdcomFacts = async () => {
      const factYear = Number(selectedFactYear);
      if (!selectedReporterCode || tableIndicatorCodes.length === 0 || !Number.isFinite(factYear) || displayedProdcomCodes.length === 0) {
        if (!cancelled) {
          setProdcomObservationsByCode({});
          setProdcomFactsMatchedCount(0);
          setProdcomFactsError(null);
          setProdcomFactsVersionCode('');
          setProdcomFactsUnmatchedCodesSample([]);
        }
        return;
      }

      setProdcomFactsLoading(true);
      setProdcomFactsError(null);
      try {
        const resp = await api.request.request<any>({
          method: 'POST',
          url: '/api/v1/prodcom/facts/by-codes',
          mediaType: 'application/json',
          body: {
            reporter_code: selectedReporterCode,
            fact_year: factYear,
            indicator_codes: tableIndicatorCodes,
            freq: 'A',
            prodcom_codes: displayedProdcomCodes,
          },
        });
        if (resp?.success === false) {
          throw new Error(resp?.message || 'Failed to fetch PRODCOM observations');
        }
        const items = resp?.items && typeof resp.items === 'object' ? resp.items : {};
        const matched = Number(resp?.metadata?.matched_code_count ?? Object.keys(items).length);
        if (!cancelled) {
          setProdcomObservationsByCode(items);
          setProdcomFactsMatchedCount(Number.isFinite(matched) ? matched : Object.keys(items).length);
          setProdcomFactsVersionCode(String(resp?.metadata?.version_code || ''));
          setProdcomFactsUnmatchedCodesSample(
            Array.isArray(resp?.metadata?.unmatched_codes_sample)
              ? resp.metadata.unmatched_codes_sample.map((code: any) => String(code))
              : []
          );
        }
      } catch (e: any) {
        if (!cancelled) {
          setProdcomObservationsByCode({});
          setProdcomFactsMatchedCount(0);
          setProdcomFactsError(e?.response?.data?.message || e?.response?.data?.detail || 'Failed to fetch PRODCOM observations');
          setProdcomFactsVersionCode('');
          setProdcomFactsUnmatchedCodesSample([]);
        }
      } finally {
        if (!cancelled) setProdcomFactsLoading(false);
      }
    };

    fetchProdcomFacts();
    return () => { cancelled = true; };
  }, [selectedReporterCode, selectedFactYear, tableIndicatorCodes, displayedProdcomCodes]);

  const diagramTitle = useMemo(() => {
    const rootName = (Array.isArray(availableRoots) && availableRoots.find(n => n.element_id === selectedElementId))?.name || 'Selected';
    return `${rootName} Value Chain in PRODCOM Codes`;
  }, [availableRoots, selectedElementId]);

  const handleNodeClick = useMemo(() => (params) => {
    const nd = params?.data?.nodeData;
    const props = nd?.properties || {};
    const members = Array.isArray(props.members) ? props.members : [];
    setSelectedGroup({ name: params?.name, description: props.prodcom_description || '', count: members.length });
    setSelectedMembers(members);
  }, []);

  const diagramLegend = useMemo(() => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>Legend (flows)</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Each technology type is mapped to a PRODCOM code. Flow weights aggregate upstream paths into grouped PRODCOM buckets.
      </Typography>
    </Box>
  ), []);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Typography variant="h6" gutterBottom>Trade Codes (PRODCOM)</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr)' }, gap: 2, mb: 2, minWidth: 0 }}>
        <Paper elevation={2} sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="body1" gutterBottom>Select a Value Chain to View its PRODCOM Codes</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Grouped view of value chains using PRODCOM codes attached to technology types.
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)' }, gap: 2, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Value chain
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select
                  displayEmpty
                  onChange={handleRootChange}
                  renderValue={(id) => {
                    if (!id) return <em>Select value chain</em>;
                    const n = (availableRoots || []).find((x) => String(x.element_id) === id);
                    return n ? n.name : '';
                  }}
                  MenuProps={SELECT_MENU_PROPS}
                  value={String(selectedRootElementId || '')}
                >
                  {(() => {
                    const existingNames = new Set((availableRoots || []).map(n => n.name));
                    const placeholders = Array.from(disabledValueChainNames).filter(n => !existingNames.has(n));
                    return (
                      <>
                        {(availableRoots || []).map((node) => {
                          const isDisabled = disabledValueChainNames.has(node.name);
                          return (
                            <MenuItem
                              key={node.element_id}
                              value={String(node.element_id)}
                              disabled={isDisabled}
                              onClick={!isDisabled ? () => handleRootChange(String(node.element_id)) : undefined}
                              sx={isDisabled ? { color: theme.palette.text.disabled } : undefined}
                            >
                              {node.name}
                            </MenuItem>
                          );
                        })}
                        {placeholders.map((name) => (
                          <MenuItem key={`disabled-${name}`} disabled sx={{ color: theme.palette.text.disabled }}>
                            {name}
                          </MenuItem>
                        ))}
                      </>
                    );
                  })()}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2">
                  N Layers
                </Typography>
                <Tooltip
                  placement="top-start"
                  title={
                    <Typography variant="body2" sx={{ maxWidth: 420 }}>
                      The number of layers defines how many steps upstream in the value chain you want to view.
                    </Typography>
                  }
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={maxIterations}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (v >= 1 && v <= 5) setMaxIterations(v);
                  }}
                  style={{ width: '60px', padding: '8px', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px', fontSize: '14px' }}
                />
              </Box>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2">
                  Min Value
                </Typography>
                <Tooltip
                  placement="top-start"
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 460 }}>
                      {'This filter removes very small flows from the chart.\n'}
                      {'Flows are first combined at PRODCOM code level, then the minimum value is applied.\n'}
                      {'In other words, it keeps meaningful grouped flows and hides minor ones.'}
                    </Typography>
                  }
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ position: 'relative', width: '84px' }}>
                <input
                  type="number"
                  min="0.1"
                  max="10"
                  step="0.1"
                  value={(minFlowThreshold * 100).toFixed(1)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (val >= 0.1 && val <= 10) setMinFlowThreshold(val / 100);
                  }}
                  style={{ width: '100%', padding: '8px 24px 8px 8px', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px', fontSize: '14px' }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    lineHeight: 1,
                  }}
                >
                  %
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            The number of layers defines how many steps upstream in the value chain you want to view.
            The minimum value threshold specifies the lowest percentage a flow must have in order to be represented.
          </Typography>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="body1" gutterBottom>Select PRODCOM Observation Filters</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Use these filters to load PRODCOM observations for all codes currently shown in the Sankey.
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)' }, gap: 2, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Reporter
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select
                  value={selectedReporterCode}
                  onChange={(e) => setSelectedReporterCode(String(e.target.value))}
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                  renderValue={(value) => {
                    if (!value) return <em>Select reporter</em>;
                    const row = availableReporters.find((r) => r.country_code === value);
                    return row ? row.country_name : String(value);
                  }}
                >
                  {availableReporters.map((reporter) => (
                    <MenuItem key={reporter.country_code} value={reporter.country_code}>
                      {reporter.country_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Year
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select
                  value={selectedFactYear}
                  onChange={(e) => setSelectedFactYear(String(e.target.value))}
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                  renderValue={(value) => value ? String(value) : <em>Select year</em>}
                >
                  {availableFactYears.map((year) => (
                    <MenuItem key={String(year.fact_year)} value={String(year.fact_year)}>
                      {year.fact_year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Indicator
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select
                  value={selectedIndicatorCode}
                  onChange={(e) => setSelectedIndicatorCode(String(e.target.value))}
                  displayEmpty
                  MenuProps={SELECT_MENU_PROPS}
                  renderValue={(value) => {
                    if (!value) return <em>Select indicator</em>;
                    const row = colorIndicatorOptions.find((i) => i.code === value);
                    return row ? row.name_en : String(value);
                  }}
                >
                  {colorIndicatorOptions.map((indicator) => (
                    <MenuItem key={indicator.code} value={indicator.code}>
                      {indicator.name_en}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Source: Eurostat. Dataset DS-059358 (
            <Link
              href="https://ec.europa.eu/eurostat/databrowser/view/ds-059358__custom_19804320/bookmark/table?lang=en&bookmarkId=24d1cf95-da28-45de-8ba0-8dac32e12a58"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mx: 0.25 }}
            >
              Eurostat Data Browser
            </Link>
            ), European Commission, 2026.
          </Typography>
          {selectorOptionsLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Loading PRODCOM filter options...
            </Typography>
          )}
          {prodcomFactsError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {prodcomFactsError}
            </Typography>
          )}
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {selectedIndicator && prodcomColorCap > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            bgcolor: 'rgba(255,255,255,0.3)',
            border: '1px solid',
            borderColor: (t) => t.palette.divider,
          }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body2">Color by: {selectedIndicator.name_en}</Typography>
            <Tooltip
              placement="top-start"
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                  {'Sankey node colors are driven by the selected PRODCOM indicator.\n'}
                  {`Scale is ${useLogColorScale ? 'logarithmic (log1p)' : 'linear'} from 0 to the maximum value among currently displayed PRODCOM codes.\n`}
                  {'Values use the selected reporter and year.'}
                </Typography>
              }
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary">Log scale</Typography>
            <Switch
              size="small"
              checked={useLogColorScale}
              onChange={(_, checked) => setUseLogColorScale(checked)}
              inputProps={{ 'aria-label': 'Toggle logarithmic color scale for PRODCOM Sankey colors' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, md: 'auto' }, width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: { xs: '100%', md: 260 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Scale: {useLogColorScale ? 'Log' : 'Linear'}
              </Typography>
            <Box
              sx={{
                width: '100%',
                height: 12,
                borderRadius: 1,
                background: 'linear-gradient(90deg, #fde725 0%, #5ec962 25%, #21918c 50%, #3b528b 75%, #440154 100%)',
              }}
            />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatLegendCompactValue(selectedIndicator.code, 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatLegendCompactValue(selectedIndicator.code, prodcomLegendMidValue)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatLegendCompactValue(selectedIndicator.code, prodcomColorCap)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {graphData && graphData.nodes && graphData.nodes.length > 0 ? (
        <>
          {!hasProdcomCodes ? (
            <Paper elevation={2} sx={{ p: 3, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No PRODCOM codes are available for this value chain.
              </Typography>
            </Paper>
          ) : (
            <SankeyDiagram
              data={graphData}
              loading={loading}
              error={error}
              title={diagramTitle}
              height={600}
              minFlowThreshold={minFlowThreshold}
              requireRootConnectivity={false}
              enablePersistentFocus
              clearFocusTrigger={clearFocusTick}
              onNodeClick={handleNodeClick}
              legend={diagramLegend}
              valueChainInfo={valueChainInfo}
              valueChainShortcomings={valueChainShortcomings}
              colorHSWhenNoBalance={false}
              prodcomValueByCode={prodcomColorByCode}
              prodcomTooltipByCode={prodcomTooltipByCode}
              prodcomTooltipByNodeKey={prodcomTooltipByNodeKey}
              codeLabelContext="prodcom"
              prodcomColorScale={useLogColorScale ? 'log' : 'linear'}
              key={`sankey-prodcom-${selectedElementId}`}
            />
          )}
        </>
      ) : (
        <Paper elevation={2} sx={{ p: 3, height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
          {loading ? <CircularProgress size={28} /> : <Typography variant="body2" color="text.secondary">No data</Typography>}
        </Paper>
      )}

      {hasProdcomCodes && displayedProdcomCodes.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mt: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              PRODCOM observations
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleExportProdcomObservations}
              disabled={prodcomFactsLoading || prodcomExportLoading || displayedProdcomRows.length === 0}
            >
              {prodcomExportLoading ? 'Exporting...' : 'Export XLSX'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Reporter: {selectedReporterCode || '—'} • Year: {selectedFactYear || '—'}
          </Typography>
          {prodcomExportError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1.5 }}>
              {prodcomExportError}
            </Typography>
          )}

          {prodcomFactsLoading ? (
            <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: `120px 120px minmax(260px,1fr) ${tableIndicatorCodes.map(() => '220px').join(' ')}`,
                  md: `140px 140px minmax(420px,1fr) ${tableIndicatorCodes.map(() => '260px').join(' ')}`,
                },
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Code</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  Value share
                  <Tooltip
                    placement="top-start"
                    title={
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                        {'Value share = (sum of Sankey path weights for links sourced from this PRODCOM code)\n'}
                        {'divided by the summed path weights across all displayed PRODCOM codes.'}
                      </Typography>
                    }
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary', lineHeight: 1 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Description</Typography>
                {tableIndicatorCodes.map((indicatorCode) => {
                  const indicator = availableIndicators.find((i) => i.code === indicatorCode);
                  return (
                    <Typography key={`head-${indicatorCode}`} variant="caption" sx={{ fontWeight: 700 }}>
                      {indicator?.name_en || indicatorCode}
                    </Typography>
                  );
                })}
              </Box>

              {displayedProdcomRows.map((row) => (
                <Box
                  key={row.code}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: `120px 120px minmax(260px,1fr) ${tableIndicatorCodes.map(() => '220px').join(' ')}`,
                      md: `140px 140px minmax(420px,1fr) ${tableIndicatorCodes.map(() => '260px').join(' ')}`,
                    },
                    gap: 1,
                    px: 1,
                    py: 0.85,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2">{row.code}</Typography>
                  <Typography variant="body2">{PERCENT_FORMATTER.format(Number(row.valueShare || 0))}</Typography>
                  <Typography variant="body2" color="text.secondary">{row.description || '—'}</Typography>
                  {tableIndicatorCodes.map((indicatorCode) => {
                    const indicatorObs = row.indicators?.[indicatorCode];
                    return (
                      <Box key={`${row.code}-${indicatorCode}`} sx={{ minWidth: 0 }}>
                        <Typography variant="body2">
                          {formatIndicatorDisplayValue(indicatorCode, indicatorObs?.value, indicatorObs?.qntunit)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {selectedGroup && (
        <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Members of {selectedGroup.name}
              </Typography>
              {selectedGroup.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedGroup.description}
                </Typography>
              )}
              <Chip label={`${selectedGroup.count} items`} size="small" />
            </Box>
            <Button variant="outlined" size="small" onClick={() => { setSelectedGroup(null); setSelectedMembers([]); setClearFocusTick(t => t + 1); }}>
              Clear selection
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {selectedMembers.map((m, idx) => (
              <ProdcomMemberCard
                key={`${m.element_id || m.id}-${idx}`}
                member={m}
                selectedProdcomVersion={resolvedGraphProdcomVersion}
                observationsByCode={prodcomObservationsByCode}
                selectedIndicator={selectedIndicator}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const ProdcomMemberCard = ({ member, selectedProdcomVersion, observationsByCode, selectedIndicator }) => {
  const p = member?.properties || {};
  // Backend returns prodcom_selected_code / prodcom_selected_description for the requested version
  const selectedEntry = getVersionProdcomEntry(p, selectedProdcomVersion);
  const selectedCode = p.prodcom_selected_code ?? selectedEntry.code;
  const selectedDescription = p.prodcom_selected_description ?? selectedEntry.description;
  const observationByIndicator = selectedCode ? observationsByCode?.[String(selectedCode)]?.indicators : null;
  const title = p.name || selectedCode || member.id;
  const funcText = p.function || '';
  const prodcomDesc = selectedDescription;
  const [expanded, setExpanded] = useState(false);

  const text = funcText || prodcomDesc || '';
  const isLong = typeof text === 'string' && text.length > 140;

  return (
    <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} gutterBottom>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {member.labels?.[0] || 'TechnologyType'} • Iteration: {p.n_iteration ?? '—'}
      </Typography>
      {selectedCode && (
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          PRODCOM code: {selectedCode}
        </Typography>
      )}
      {selectedIndicator && (
        <Box sx={{ mb: 1 }}>
          <Typography key={`${selectedCode}-${selectedIndicator.code}`} variant="body2">
            {selectedIndicator.name_en}: {formatIndicatorDisplayValue(selectedIndicator.code, observationByIndicator?.[selectedIndicator.code]?.value, observationByIndicator?.[selectedIndicator.code]?.qntunit)}
          </Typography>
        </Box>
      )}
      {text && (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={expanded ? {} : {
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
            }}
          >
            {text}
          </Typography>
          {isLong && (
            <IconButton size="small" onClick={() => setExpanded(v => !v)} aria-label={expanded ? 'Collapse' : 'Expand'} sx={{ mt: 0.5 }}>
              {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
          )}
        </>
      )}
    </Paper>
  );
};

export default ProdcomGroupedProdcomCodeAnalysis;
